/**
 * The rules a schema names rather than describes.
 *
 * Most constraints are self-contained — a bound, a pattern, a comparison against a named field —
 * and `validate.ts` evaluates them without knowing what they mean. These are the ones that cannot
 * be written that way: a setting that looks active and does nothing, which depends on two or three
 * keys at once and on which of them was set. The catalog names the rule
 * (`ParamSpec.validator_name`) and this is where the name is answered.
 *
 * Every rule here is also enforced by backend-api, which stays the only gate. These exist so a
 * carer is told before the save rather than by it.
 *
 * A name with no entry here is not an error (see `validate.ts`): the configuration service may
 * declare a rule this dashboard has not learned, and refusing to save on a rule we cannot evaluate
 * would be worse than letting the backend answer
 */

import { FieldValue, FieldValues } from '../schema';

/** True when the value is acceptable. */
export type NamedRule = (value: FieldValue, values: FieldValues) => boolean;

const isEmpty = (value: FieldValue): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

export const NAMED_RULES: Record<string, NamedRule> = {
  indoorGeofencingNeedsZones: (value, values) => {
    if (values['indoor-geofencing-enabled'] !== true) return true;
    return !isEmpty(value) || !isEmpty(values['indoor-geofencing-preset']);
  },

  geoNeedsPerimeter: (value, values) => values['outdoor-geofencing-mode'] !== 'geo' || !isEmpty(value),

  validateGeoJsonPolygon: (value) => {
    if (isEmpty(value)) return true;
    if (typeof value !== 'object' || Array.isArray(value)) return false;
    const feature = value as { type?: unknown; geometry?: { type?: unknown; coordinates?: unknown } };
    if (feature.type !== 'Feature' || feature.geometry?.type !== 'Polygon') return false;
    const rings = feature.geometry?.coordinates;
    if (!Array.isArray(rings) || rings.length === 0) return false;
    return rings.every(
      (ring) =>
        Array.isArray(ring) &&
        // A ring needs three distinct corners to enclose anything; GeoJSON repeats the first as
        // the last, so a closed one is four.
        ring.length >= 3 &&
        ring.every((point) => Array.isArray(point) && point.length >= 2 && point.every((n) => typeof n === 'number'))
    );
  },
};
